// @flow
import styles from './JobListItem.less';
import { get } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import ScrollMonitor from 'react-scrollmonitor';

import { jobLogoUrlPrefix } from 'src/config';
import { parseHTML } from 'autosuggest-highlight';
import striptags from 'striptags';
import {
  ScreenReaderOnly,
  Hidden,
  Text,
  Highlight
} from 'seek-style-guide/react';
import MetaHierarchy from './MetaHierarchy/MetaHierarchy';
import { jobViewModel } from '@seek/seek-refine-job-search';
import NavLink from 'src/components/NavLink/NavLink';
import SaveJob from 'src/components/SaveJob/SaveJob';
import { trackJobClick } from 'src/modules/job-tracking';
import { resultLinkClicked } from 'src/store/results/results';
import { prepopulateJob } from 'src/store/jobdetails/jobdetails';
import {
  jobListItemImpression,
  jobListItemImpressionExit
} from 'src/store/joblistitem/joblistitem';
import { locationFromHref } from '@seek/seek-jobs-seo/build/location';
import MetaLinkRenderer from './MetaLinkRenderer/MetaLinkRenderer';
import { CompanyLink } from '../CompanyLink';
import withSolImpressionTracker from 'src/modules/solTracker/withSolImpressionTracker';

type JobClickedFunction = (job: SearchResultJob) => Function;
type PrePopulateJobDetailsFunction = (job: SearchResultJob) => Function;

type Props = {
  job: SearchResultJob,
  isFirst: boolean,
  isLast: boolean,
  isCompanySerp: boolean,
  query: SearchParams,
  location?: RawSearchApiLocation,
  locale: Locale,
  jobClicked: JobClickedFunction,
  prepopulateJobDetails: PrePopulateJobDetailsFunction,
  jobListItemImpression: Function,
  jobListItemImpressionExit: Function,
  sessionId: ?string,
  solMetadata: *
};

function highlightFromHTML(html, isDescription) {
  return parseHTML(html, 'b').map(({ highlight, text }, index) => {
    const cleanText = striptags(text);

    return highlight ? (
      <Highlight
        key={index}
        data-automation="jobItem-highlight"
        secondary={isDescription}
      >
        {cleanText}
      </Highlight>
    ) : (
      cleanText
    );
  });
}

function getJobDataAutomation(isPremium, locationMatch) {
  if (isPremium) {
    return 'premiumJob';
  }

  if (locationMatch === 'Exact') {
    return 'tier1job';
  }

  if (locationMatch === 'Nearby') {
    return 'tier2job';
  }

  if (locationMatch === 'Area') {
    return 'tier3job';
  }

  return 'normalJob';
}

function sameJob(prevJob, nextJob) {
  return nextJob && prevJob && nextJob.id === prevJob.id;
}

function sameLocation(prevLocation, nextLocation) {
  return (
    nextLocation &&
    prevLocation &&
    nextLocation.locationId === prevLocation.locationId
  );
}

const mapStateToProps = ({ search, results, location, user }) => ({
  query: search.query,
  locale: location.locale,
  location: results.location,
  requiresSolMonitor: results.requiresSolMonitor,
  sessionId: user.sessionId,
  isCompanySerp: Boolean(get(results, 'results.searchParams.companysearch'))
});

const mapDispatchToProps = {
  jobClicked: resultLinkClicked,
  prepopulateJobDetails: prepopulateJob,
  jobListItemImpression,
  jobListItemImpressionExit
};

class JobListItem extends Component<Props> {
  shouldComponentUpdate(nextProps) {
    return (
      !sameJob(this.props.job, nextProps.job) ||
      !sameLocation(this.props.location, nextProps.location) ||
      this.props.isLast !== nextProps.isLast
    );
  }

  // Track ENTER/EXIT events so that EXIT events aren't tracked without a
  // corresponding ENTER event.
  inViewport = false;

  resultImpression = () => {
    // NOTE: ScrollMonitor (see `on` method in scrollmonitor used by react-scrollmonitor)
    //       will trigger EXIT events, at DOM element mount time, for items fully above the viewport.
    //       There will be no corresponding ENTER event for these.
    //
    // Track entry of the JobItem into the viewport. EXIT events will be ignored until this is true.
    this.inViewport = true;

    this.props.jobListItemImpression({ token: this.props.job.tracking });
  };

  resultImpressionExit = () => {
    const {
      job: { tracking }
    } = this.props;

    // Track EXIT events if the DOM element has already entered the viewport.
    if (this.inViewport) {
      this.inViewport = false;

      this.props.jobListItemImpressionExit({ token: tracking });
    }
  };

  resultClick = () => {
    const { jobClicked, job, prepopulateJobDetails } = this.props;

    trackJobClick(job.tracking);
    prepopulateJobDetails(job);
    return jobClicked(job);
  };

  render() {
    const {
      isFirst,
      isLast,
      query,
      locale,
      isCompanySerp,
      location: locationProp,
      job
    } = this.props;

    const {
      id,
      isPremium,
      isStandOut,
      title,
      logo,
      isFresh,
      listingDate,
      listingDateDescription,
      bulletPoints,
      teaser,
      classification,
      salary,
      workType,
      joraImpressionTrackingUrl,
      location: jobLocation,
      locationMatch
    }: JobViewModel = jobViewModel({ job, query, locationProp, locale });

    const jobClassName = classnames({
      [styles.root]: true,
      [styles.root_isPremium]: isPremium,
      [styles.root_isStandOut]: isStandOut,
      [styles.root_isClassic]: !isPremium && !isStandOut,
      [styles.root_isFirst]: isFirst,
      [styles.root_isCompanySerp]: isCompanySerp,
      [styles.root_isLast]: isLast
    });

    const jdpNavLinkProps = {
      preNavigationHooks: [this.resultClick],
      location: {
        ...locationFromHref(`/job/${id}`),
        query: {
          type: job.displayType,
          searchRequestToken: get(job, 'solMetadata.searchRequestToken')
        }
      }
    };

    const clickableWrapperProps = {
      ...jdpNavLinkProps,
      component: 'div',
      className: styles.tappableJob
    };

    /* eslint-disable react/no-danger */
    return (
      <ScrollMonitor
        enterViewport={this.resultImpression}
        exitViewport={this.resultImpressionExit}
      >
        <article
          aria-label={striptags(title)}
          data-job-id={id}
          className={jobClassName}
          data-automation={getJobDataAutomation(isPremium, locationMatch)}
        >
          <Hidden desktop>
            <NavLink {...clickableWrapperProps} />
          </Hidden>

          {joraImpressionTrackingUrl && (
            <img
              src={joraImpressionTrackingUrl}
              className={styles.trackingPixel}
            />
          )}

          <Text heading raw className={styles.onTopOfMobileOverlay}>
            <h1>
              <NavLink
                {...jdpNavLinkProps}
                className={styles.titleLink}
                data-automation="jobTitle"
              >
                {highlightFromHTML(title)}
              </NavLink>
            </h1>
          </Text>

          <ScreenReaderOnly>
            {isPremium ? 'This is a featured job' : listingDateDescription}
          </ScreenReaderOnly>
          <Text
            small
            raw
            className={classnames({
              [styles.badge]: true,
              [styles.fresh]: !isPremium && isFresh,
              [styles.featured]: isPremium
            })}
          >
            <span
              aria-hidden="true"
              data-automation={isPremium ? 'jobPremium' : 'jobListingDate'}
            >
              {isPremium ? 'Featured' : listingDate}
            </span>
          </Text>

          <Text small>
            <ScreenReaderOnly>at </ScreenReaderOnly>
            <CompanyLink job={job} />
          </Text>

          <div className={styles.section}>
            <ScreenReaderOnly>
              <p>This is a {workType} job</p>
            </ScreenReaderOnly>

            {jobLocation ? (
              <MetaHierarchy
                type="location"
                meta={jobLocation}
                renderer={MetaLinkRenderer}
              />
            ) : null}

            {salary ? (
              <Text
                small
                strong
                data-automation="jobSalary"
                aria-label={`Salary: ${salary}`}
              >
                {salary}
              </Text>
            ) : null}

            <Hidden mobile>
              <MetaHierarchy
                type="classification"
                meta={classification}
                renderer={MetaLinkRenderer}
              />
            </Hidden>
          </div>

          {bulletPoints ? (
            <ul className={styles.section}>
              {bulletPoints.map((option, i) => (
                <li className={styles.bullet} key={i}>
                  <Text small raw>
                    {highlightFromHTML(option)}
                  </Text>
                </li>
              ))}
            </ul>
          ) : null}

          <Text
            small
            secondary
            className={classnames({
              [styles.section]: true,
              [styles.descriptionWithLogo]: logo.description
            })}
            data-automation="jobShortDescription"
          >
            {highlightFromHTML(teaser, true)}
          </Text>

          <div className={styles.footer}>
            <div className={styles.leftColumn}>
              <SaveJob
                className={styles.onTopOfMobileOverlay}
                withAppliedTick
                buttonCopy={{
                  saved: 'Saved',
                  unsaved: 'Save',
                  applied: 'Applied'
                }}
                job={job}
              />
            </div>

            {logo.description ? (
              <div className={styles.logoContainer}>
                <img
                  data-automation="jobLogo"
                  src={`${jobLogoUrlPrefix}/${logo.description}`}
                  alt="Company logo"
                  aria-hidden="true"
                />
              </div>
            ) : null}
          </div>
        </article>
      </ScrollMonitor>
    );
    /* eslint-enable react/no-danger */
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withSolImpressionTracker(JobListItem));
